import { AdditionalTaskInfo } from "./taskInfo/AdditionalTaskInfo";
import { autonomousActivityTypeToJSON } from "protos/atk/enums";
import { Button } from "@mui/material";
import { classes } from "common/theme/theme";
import { DateTime } from "luxon";
import { ExpectedTractorState } from "./taskInfo/ExpectedTractorState";
import { getTaskTypeName, jobStateIcon } from "common/utils/job";
import { jobExplorer, JobSelectionType } from "common/state/jobExplorer";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { Section } from "portal/components/map/navigation/Section";
import {
  State,
  stateToJSON,
  Task,
  taskDesignationToJSON,
} from "protos/rtc/jobs";
import { TaskUpdate } from "protos/rtc/web_socket";
import { useDispatch } from "react-redux";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useTaskSubscription } from "common/hooks/useLiveJobData";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InterventionIcon from "@mui/icons-material/NewReleases";
import ManualIcon from "@mui/icons-material/Engineering";
import NextIcon from "@mui/icons-material/SkipNext";
import PreviousIcon from "@mui/icons-material/SkipPrevious";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

interface TaskDetailsProps {
  task: Task;
}
export const TaskDetails: FC<TaskDetailsProps> = ({ task: inputTask }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [taskResponse, setTask] = useState<Task>();
  const onTaskUpdate = useCallback((t: TaskUpdate) => {
    setTask(t.task);
  }, []);
  useTaskSubscription({
    taskId: inputTask.id.toString(),
    onTaskUpdate,
  });
  const [getObjectiveTasks, { data: objectiveTasks }] =
    rtcJobsApi.endpoints.listObjectiveTasks.useLazyQuery();
  const currentTaskIndex = useMemo(
    () => objectiveTasks?.tasks.findIndex((tsk) => tsk.id === inputTask.id),
    [objectiveTasks, inputTask.id]
  );
  const previousTask =
    objectiveTasks && currentTaskIndex
      ? objectiveTasks.tasks[currentTaskIndex - 1]
      : undefined;
  const nextTask =
    objectiveTasks &&
    currentTaskIndex !== undefined &&
    currentTaskIndex < objectiveTasks.tasks.length - 1
      ? objectiveTasks.tasks[currentTaskIndex + 1]
      : undefined;

  const [setTaskState] = useMutationPopups(
    rtcJobsApi.endpoints.setTaskState.useMutation()
  );
  const task = taskResponse ?? inputTask;

  const { data: interventions } = useQueryPopups(
    rtcJobsApi.endpoints.listInterventions.useQuery(
      { taskIds: `${task.id}` },
      { refetchOnMountOrArgChange: 5, pollingInterval: 5000 }
    )
  );

  const canReopenTask = useCallback(
    (t: Task) => {
      let anyClosedTasksAfterT = false;

      // if for some reason this task isn't in the list of tasks on this
      // objective, just assume that it can be reopened on the grounds that it's
      // probably better to let the ROCC reopen tasks that they shouldn't than to
      // block them
      if (currentTaskIndex !== undefined && currentTaskIndex !== -1) {
        for (
          let i = currentTaskIndex + 1;
          i < (objectiveTasks?.tasks ?? []).length;
          i++
        ) {
          if (
            objectiveTasks?.tasks[i]?.state === State.COMPLETED ||
            objectiveTasks?.tasks[i]?.state === State.FAILED
          ) {
            anyClosedTasksAfterT = true;
          }
        }
      }

      return t.state === State.COMPLETED && !anyClosedTasksAfterT;
    },
    [currentTaskIndex, objectiveTasks?.tasks]
  );

  // used to show a live-updating timer for how long the task has been active for.
  const [now, setNow] = useState(DateTime.now());
  const endTime = task.endedAt ? DateTime.fromISO(task.endedAt) : now;

  useEffect(() => {
    const nowTimer = setInterval(() => {
      setNow(DateTime.now());
    }, 1000);
    return () => {
      clearInterval(nowTimer);
    };
  }, []);
  const JobStateIcon = jobStateIcon(task.state);

  // once per objective, not on every update to this task
  const objectiveId = task.objectiveId;
  useEffect(() => {
    if (objectiveId) {
      getObjectiveTasks({ objectiveId });
    }
  }, [objectiveId, getObjectiveTasks]);

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="flex flex-row p-2 gap-2">
        <Button
          variant="contained"
          color="secondary"
          className="flex-1"
          disabled={!previousTask}
          startIcon={<PreviousIcon />}
          onClick={() =>
            dispatch(
              jobExplorer.actions.setSelection(
                previousTask
                  ? {
                      type: JobSelectionType.TASK,
                      selection: previousTask,
                    }
                  : undefined
              )
            )
          }
        >
          {t("utils.actions.previous")}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          className="flex-1"
          disabled={!nextTask}
          endIcon={<NextIcon />}
          onClick={() =>
            dispatch(
              jobExplorer.actions.setSelection(
                nextTask
                  ? {
                      type: JobSelectionType.TASK,
                      selection: nextTask,
                    }
                  : undefined
              )
            )
          }
        >
          {t("utils.actions.next")}
        </Button>
      </div>
      <h2 className="flex gap-1 mx-4">
        <JobStateIcon className="flex my-auto" />
        <div>{stateToJSON(task.state)}</div>
      </h2>
      <div className="flex justify-between mx-4">
        <div>{t("utils.descriptors.id")}</div>
        {task.id}
      </div>
      <div className="flex justify-between mx-4">
        <div>{t("utils.descriptors.type_one")}</div>
        {getTaskTypeName(t, task)}
      </div>
      <div className="flex justify-between mx-4">
        <div>{t("views.autotractor.taskDetails.taskDesignation")}</div>
        {task.metadata?.designation ? (
          <code>{taskDesignationToJSON(task.metadata.designation)}</code>
        ) : (
          <>&mdash;</>
        )}
      </div>
      <div className="flex justify-between mx-4">
        <div>{t("views.autotractor.taskDetails.autonomousActivity")}</div>
        {task.metadata?.activityType ? (
          <code>
            {autonomousActivityTypeToJSON(task.metadata.activityType)}
          </code>
        ) : (
          <>&mdash;</>
        )}
      </div>
      <div className="flex justify-between mx-4">
        <div>{t("utils.descriptors.startedAt")}</div>
        {task.startedAt &&
          DateTime.fromISO(task.startedAt).toLocaleString(
            {
              dateStyle: "short",
              timeStyle: "medium",
            },
            { locale: i18n.language }
          )}
      </div>
      <div className="flex justify-between mx-4">
        <div>{t("utils.descriptors.endedAt")}</div>
        {task.endedAt &&
          DateTime.fromISO(task.endedAt).toLocaleString(
            {
              dateStyle: "short",
              timeStyle: "medium",
            },
            { locale: i18n.language }
          )}
      </div>
      <div className="flex justify-between mx-4">
        <div>{t("utils.descriptors.duration")}</div>
        {task.startedAt
          ? endTime.diff(DateTime.fromISO(task.startedAt)).toFormat("hh:mm:ss")
          : "Not Started"}
      </div>
      <div className="flex justify-between mx-4">
        <div className="flex gap-2">
          <ManualIcon />
          <div>{t("models.autotractor.manuallyAssisted")}</div>
        </div>
        {task.manuallyAssisted ? <CheckIcon /> : <CloseIcon />}
      </div>
      <div className="flex flex-col">
        <Section header={t("views.autotractor.taskCriteria.taskCriteria")}>
          <div className="flex flex-col justify-between px-4 py-2">
            <ExpectedTractorState states={task.expectedTractorState} />
          </div>
        </Section>
        <AdditionalTaskInfo task={task} />
        <Section header={t("models.autotractor.intervention_other")}>
          <div className="flex flex-col p-2">
            {interventions?.interventions.map((i) => (
              <div key={i.id} className="flex mx-2 gap-4">
                <InterventionIcon
                  fontSize="small"
                  className={classes(
                    "flex my-auto",
                    i.state === State.COMPLETED && "text-green-300"
                  )}
                />
                <div
                  className={classes(
                    i.state === State.COMPLETED && "line-through text-green-300"
                  )}
                >
                  {i.description}
                  {i.taskId}
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>
      <div className="flex flex-col mt-auto p-2 gap-1">
        <Button
          variant="contained"
          disabled={!canReopenTask(task)}
          onClick={() =>
            setTaskState({
              id: task.id,
              state: task.sequence ? State.IN_PROGRESS : State.PENDING,
              override: true,
            })
          }
        >
          {t("views.autotractor.reopenTask")}
        </Button>
        {!task.sequence && (
          <Button
            variant="contained"
            disabled={task.state === State.COMPLETED}
            onClick={() =>
              setTaskState({
                id: task.id,
                state: State.COMPLETED,
                override: true,
              })
            }
          >
            {t("views.autotractor.actions.markComplete")}
          </Button>
        )}
      </div>
    </div>
  );
};
